import { useEffect, useState } from "react";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { getBoxes } from "@/services/box/get-boxes";
import { createBox } from "@/services/box/create-box";
import { PokemonBoxData } from "types/web-app";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { RouteNames } from "@/types/route-names";
import { Alert } from "@/components/alert/alert";
import { BackIcon, AddIcon } from "@/icons";

export const BoxListView = () => {
  const { getAccessToken } = useAuth();
  const { setCurrentRoute, setIntent } = useSPAContext();
  const [boxes, setBoxes] = useState<PokemonBoxData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newBoxName, setNewBoxName] = useState("");

  const fetchBoxes = async () => {
    const token = getAccessToken();
    if (!token) return;

    setIsLoading(true);
    try {
      const response = await getBoxes(token);
      if (response.ok) {
        setBoxes(response.data.boxes);
      }
    } catch (error) {
      console.error(error);
      Alert("Failed to fetch boxes");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBoxes();
  }, []);

  const handleCreateBox = async () => {
    if (!newBoxName.trim()) return;

    const token = getAccessToken();
    if (!token) return;

    setIsCreating(true);
    try {
      const response = await createBox(token, { name: newBoxName });
      if (response.ok && response.data.box) {
        setBoxes((prev) => [...prev, response.data.box!]);
        setNewBoxName("");
        Alert("Box created successfully");
      } else {
        Alert(response.message || "Failed to create box");
      }
    } catch (error) {
      console.error(error);
      Alert("Failed to create box");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="w-full h-full p-4 flex flex-col gap-4">
      <StandardFrame variant="purple" className="flex flex-col gap-4 h-full">
        <div className="flex items-center justify-between border-b border-white/10 pb-2">
          <h2 className="text-xl font-bold text-white uppercase tracking-wider">
            Storage Boxes
          </h2>
          <SimpleButton
            onClick={() => setCurrentRoute(undefined)}
            variant="red"
          >
            <BackIcon width={24} height={24} />
          </SimpleButton>
        </div>

        {/* Create Box Form */}
        <div className="flex gap-2 p-2 bg-black/20 rounded">
          <input
            type="text"
            value={newBoxName}
            onChange={(e) => setNewBoxName(e.target.value)}
            placeholder="New Box Name"
            className="flex-1 bg-black/40 border border-white/20 rounded px-3 py-2 text-white placeholder-white/50 focus:outline-none focus:border-pk-orange-4"
          />
          <button
            onClick={handleCreateBox}
            disabled={isCreating || !newBoxName.trim()}
            className="!p-0"
          >
            <AddIcon width={36} height={36} className="invert" />
          </button>
        </div>

        {/* Box List */}
        <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-3 p-1">
          {isLoading ? (
            <div className="col-span-2 text-center text-white/50">
              Loading...
            </div>
          ) : boxes.length === 0 ? (
            <div className="col-span-2 text-center text-white/50">
              No boxes created yet.
            </div>
          ) : (
            boxes.map((box) => (
              <div
                key={box.id}
                className="bg-pk-gray-2 h-16 border-2 border-pk-gray-4 rounded-lg p-2 flex flex-col gap-1 cursor-pointer hover:bg-pk-gray-1 active:scale-95 transition-all"
                onClick={() => {
                  window.localStorage.setItem("selected_box_id", box.id);
                  setCurrentRoute(RouteNames.BOX_DETAILS);
                }}
                style={{
                  backgroundColor: box.color || "#ccc",
                }}
              >
                <div className="absolute top-0 right-0 p-1 opacity-50 group-hover:opacity-100">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: box.color || "#ccc" }}
                  />
                </div>
                <span className="font-bold text-white text-[9px] truncate">
                  {box.name} {box.color}
                </span>
                <div className="flex items-end justify-between">
                  <span className="text-[10px] text-white/50">
                    {box.pokemon_count || 0} items
                  </span>
                  <img
                    src={`/assets/icons/boxes/${box.icon}.svg`}
                    className="w-8 h-8 opacity-80 fill-white stroke-white"
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </StandardFrame>
    </div>
  );
};
